import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { supabase } from "../config/supabaseClient";
import { statuses, POPULATION_GROUPS } from "../constant/constants";

// Async thunk to fetch company user demographics
export const fetchCompanyUserDemographics = createAsyncThunk(
  "bBbeeConfiguration/fetchCompanyUserDemographics",
  async (company_id, { rejectWithValue }) => {
    try {
      // Fetch normalized columns (gender, race)
      const { data, error } = await supabase
        .from("company_user")
        .select("gender, race")
        .eq("company_id", company_id)
        .eq("status", statuses.ACTIVE);
      
      if (error) {
        console.error("Fetch demographics error:", error.message);
        throw error;
      }
      
      // Initialize demographics with all POPULATION_GROUPS (with zeros)
      const demographics = {};
      POPULATION_GROUPS.forEach(group => {
        demographics[group.key] = { male: 0, female: 0, total: 0 };
      });
      
      if (!data || data.length === 0) {
        // Return demographics with all zeros so the component can still display the table
        return demographics;
      }
      
      // Process the data to count by race and gender
      data.forEach(user => {
        // Use normalized columns (gender, race)
        const race = user.race || null;
        const gender = user.gender || null;
        
        // Find matching POPULATION_GROUP by comparing in lowercase
        let matchedRaceKey = null;
        if (race) {
          const raceLower = String(race).trim().toLowerCase();
          const matchedGroup = POPULATION_GROUPS.find(g => 
            g.key.toLowerCase() === raceLower || g.label.toLowerCase() === raceLower
          );
          matchedRaceKey = matchedGroup ? matchedGroup.key : null;
        }
        
        // Normalize gender value
        let normalizedGender = gender ? String(gender).toLowerCase().trim() : 'other';
        
        // Normalize gender to male/female/other (handle variations)
        if (normalizedGender.includes('female') || (normalizedGender.startsWith('f') && !normalizedGender.includes('male'))) {
          normalizedGender = 'female';
        } else if (normalizedGender.includes('male') || normalizedGender.startsWith('m')) {
          normalizedGender = 'male';
        } else {
          normalizedGender = 'other';
        }
        
        // Only count if race matches a POPULATION_GROUP
        if (matchedRaceKey && demographics[matchedRaceKey]) {
          if (normalizedGender === 'male') {
            demographics[matchedRaceKey].male++;
          } else if (normalizedGender === 'female') {
            demographics[matchedRaceKey].female++;
          }
          demographics[matchedRaceKey].total++;
        }
      });
      
      return demographics;
    } catch (err) {
      console.error("Fetch demographics catch error:", err.message);
      return rejectWithValue(err.message);
    }
  }
);

// Async thunk to save BBBEE budget configuration
export const saveBBBEEBudgetConfiguration = createAsyncThunk(
  "bBbeeConfiguration/saveBBBEEBudgetConfiguration",
  async ({budgetData, yearId, companyId}, { rejectWithValue }) => {
   
    try {
      const { data: responseData, error } = await supabase
        .from("configurations")
        .update({ demographic_config: budgetData.demographic_config, total_budget: budgetData.total_budget })
        .eq("company_id", companyId)
        .eq("year_id", yearId)
        .select();
      
      if (error) {
        console.error("Save BBBEE budget error:", error.message);
        throw error;
      }
      return responseData;
    } catch (err) {
      console.error("Save BBBEE budget catch error:", err.message);
      return rejectWithValue(err.message);
    }
  }
);

// Async thunk to fetch BBBEE budget configuration
export const fetchBBBEEBudgetConfiguration = createAsyncThunk(
  "bBbeeConfiguration/fetchBBBEEBudgetConfiguration",
  async ({yearId, companyId}, { rejectWithValue }) => {
    try {
      const { data, error } = await supabase
        .from("configurations")
        .select("demographic_config, total_budget")
        .eq("company_id", companyId)
        .eq("year_id", yearId)
        .maybeSingle();
      
      if (error) {
        console.error("Fetch BBBEE budget error:", error.message);
        throw error;
      }
      return data;
    } catch (err) {
      console.error("Fetch BBBEE budget catch error:", err.message);
      return rejectWithValue(err.message);
    }
  }
);

// Async thunk to fetch EAP configuration for a region
export const fetchEAPConfiguration = createAsyncThunk(
  "bBbeeConfiguration/fetchEAPConfiguration",
  async ({ provinceName, isNational }, { rejectWithValue }) => {
    try {
      
      let data, error;
      if (isNational) {
        // For national data, query with null province_name
        const result = await supabase
          .from("eap_configuration")
          .select("*")
          .is("province_name", null)
          .eq("is_national", true);
        data = result.data;
        error = result.error;
      } else {
        // For province data, query with specific province_name
        const result = await supabase
          .from("eap_configuration")
          .select("*")
          .eq("province_name", provinceName)
          .eq("is_national", false);
        data = result.data;
        error = result.error;
      }
      
      if (error) {
        console.error("Fetch EAP configuration error:", error.message);
        throw error;
      }
      return data;
    } catch (err) {
      console.error("Fetch EAP configuration catch error:", err.message);
      return rejectWithValue(err.message);
    }
  }
);

const bBbeeConfigurationSlice = createSlice({
  name: "bBbeeConfiguration",
  initialState: {
    demographics: {},
    budgetConfiguration: null,
    eapConfiguration: [],
    eapLoading: false,
    eapError: null,
    loading: false,
    error: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearDemographics: (state) => {
      state.demographics = {};
    },
    clearBudgetConfiguration: (state) => {
      state.budgetConfiguration = null;
    },
    clearEAPConfiguration: (state) => {
      state.eapConfiguration = [];
      state.eapError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Company User Demographics cases
      .addCase(fetchCompanyUserDemographics.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCompanyUserDemographics.fulfilled, (state, action) => {
        state.loading = false;
        state.demographics = action.payload;
      })
      .addCase(fetchCompanyUserDemographics.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Save BBBEE Budget Configuration cases
      .addCase(saveBBBEEBudgetConfiguration.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(saveBBBEEBudgetConfiguration.fulfilled, (state, action) => {
        state.loading = false;
        // update().select() returns an array; fetch uses .single() — normalize so Dashboard reads the same shape.
        const p = action.payload;
        state.budgetConfiguration =
          Array.isArray(p) && p.length > 0 ? p[0] : p;
      })
      .addCase(saveBBBEEBudgetConfiguration.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch BBBEE Budget Configuration cases
      .addCase(fetchBBBEEBudgetConfiguration.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBBBEEBudgetConfiguration.fulfilled, (state, action) => {
        state.loading = false;
        state.budgetConfiguration = action.payload;
      })
      .addCase(fetchBBBEEBudgetConfiguration.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch EAP Configuration cases
      .addCase(fetchEAPConfiguration.pending, (state) => {
        state.eapLoading = true;
        state.eapError = null;
      })
      .addCase(fetchEAPConfiguration.fulfilled, (state, action) => {
        state.eapLoading = false;
        state.eapConfiguration = action.payload;
      })
      .addCase(fetchEAPConfiguration.rejected, (state, action) => {
        state.eapLoading = false;
        state.eapError = action.payload;
      });
  },
});

export const { clearError, clearDemographics, clearBudgetConfiguration, clearEAPConfiguration } = bBbeeConfigurationSlice.actions;
export default bBbeeConfigurationSlice.reducer; 